import { EdgeLocation } from '@cast/types-omni';

import { EdgeLocationAddonName } from '../../types';
import { EDGE_LOCATION_ADDON_NAME_TO_DISPLAY_NAME_MAP } from '../../constants';

import { isEdgeLocationAddonEnabled } from '../../_utils';

export type GpuFeatureAvailability =
  | { available: true }
  | { available: false; reason: string };

export const getMigAvailability = ({
  edgeLocation,
}: {
  edgeLocation: EdgeLocation;
}): GpuFeatureAvailability => {
  const isGpuOperatorAddonEnabled = isEdgeLocationAddonEnabled({
    edgeLocation,
    addonName: EdgeLocationAddonName.NvidiaGpuOperator,
  });

  return isGpuOperatorAddonEnabled
    ? { available: true }
    : {
        available: false,
        reason: `MIG requires the ${
          EDGE_LOCATION_ADDON_NAME_TO_DISPLAY_NAME_MAP[
            EdgeLocationAddonName.NvidiaGpuOperator
          ]
        } add-on on this edge location.`,
      };
};

export const getGpuTimeSlicingAvailability = ({
  edgeLocation,
}: {
  edgeLocation: EdgeLocation;
}): GpuFeatureAvailability => {
  const isGpuOperatorAddonEnabled = isEdgeLocationAddonEnabled({
    edgeLocation,
    addonName: EdgeLocationAddonName.NvidiaGpuOperator,
  });
  const isDraAddonEnabled = isEdgeLocationAddonEnabled({
    edgeLocation,
    addonName: EdgeLocationAddonName.NvidiaDra,
  });

  if (!isGpuOperatorAddonEnabled) {
    return {
      available: false,
      reason: `GPU time-slicing requires the ${
        EDGE_LOCATION_ADDON_NAME_TO_DISPLAY_NAME_MAP[
          EdgeLocationAddonName.NvidiaGpuOperator
        ]
      } add-on on this edge location.`,
    };
  }

  if (isDraAddonEnabled) {
    return {
      available: false,
      reason: `GPU time-slicing is not available with the ${
        EDGE_LOCATION_ADDON_NAME_TO_DISPLAY_NAME_MAP[
          EdgeLocationAddonName.NvidiaDra
        ]
      } add-on enabled on this edge location.`,
    };
  }

  return { available: true };
};

/**
 * Canonical MIG profile lists per NVIDIA GPU model.
 * Source: https://docs.nvidia.com/datacenter/tesla/mig-user-guide/latest/supported-mig-profiles.html
 */
const MIG_PROFILES_BY_NVIDIA_GPU_MODEL: Record<string, string[]> = {
  A100_40GB: [
    '1g.5gb',
    '1g.5gb+me',
    '1g.10gb',
    '2g.10gb',
    '3g.20gb',
    '4g.20gb',
    '7g.40gb',
  ],
  A100_80GB: [
    '1g.10gb',
    '1g.10gb+me',
    '1g.20gb',
    '2g.20gb',
    '3g.40gb',
    '4g.40gb',
    '7g.80gb',
  ],
  H100_80GB: [
    '1g.10gb',
    '1g.10gb+me',
    '1g.20gb',
    '2g.20gb',
    '3g.40gb',
    '4g.40gb',
    '7g.80gb',
  ],
  H200_141GB: [
    '1g.18gb',
    '1g.18gb+me',
    '1g.35gb',
    '2g.35gb',
    '3g.71gb',
    '4g.71gb',
    '7g.141gb',
  ],
  B200_180GB: [
    '1g.23gb',
    '1g.23gb+me',
    '1g.45gb',
    '2g.45gb',
    '3g.90gb',
    '4g.90gb',
    '7g.180gb',
  ],
  // Source: the MIG user guide has no HGX B300 table; profile names are from
  // https://docs.nvidia.com/ai-enterprise/release-8/latest/infra-software/vgpu/reference/blackwell.html
  B300_279GB: [
    '1g.34gb',
    '1g.34gb+me',
    '1g.67gb',
    '2g.67gb',
    '3g.135gb',
    '4g.135gb',
    '7g.269gb',
  ],
  RTX_PRO_6000_96GB: [
    '1g.24gb',
    '1g.24gb+me',
    '1g.24gb+gfx',
    '1g.24gb+me.all',
    '1g.24gb-me',
    '2g.48gb',
    '2g.48gb+gfx',
    '2g.48gb+me.all',
    '2g.48gb-me',
    '4g.96gb',
    '4g.96gb+gfx',
  ],
};

// Nebius instance types are named `platform.preset` (e.g.
// 'gpu-h100-sxm.8gpu-128vcpu-1600gb') — presets only vary vCPU/RAM/GPU count,
// which doesn't affect MIG. Keys are therefore platforms, matched by prefix,
// so all presets and region variants (e.g. 'gpu-b200-sxm-a') are covered.
const MIG_PARTITIONS_BY_NEBIUS_PLATFORM: Record<string, string[]> = {
  'gpu-b200-sxm': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.B200_180GB,
  'gpu-b300-sxm': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.B300_279GB,
  'gpu-h100-sxm': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H100_80GB,
  'gpu-h200-sxm': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H200_141GB,
  'gpu-rtx6000': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.RTX_PRO_6000_96GB,
};

/**
 * MIG partition sizes per cloud instance type.
 * Keys are lowercase since providers return mixed case (e.g. Oracle's 'BM.GPU4.8').
 * Exact instance type match only; Nebius is handled separately by MIG_PARTITIONS_BY_NEBIUS_PLATFORM.
 */
const MIG_PARTITIONS_BY_INSTANCE_TYPE: Record<string, string[]> = {
  // AWS
  'p4d.24xlarge': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.A100_40GB,
  'p4de.24xlarge': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.A100_80GB,
  'p5.48xlarge': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H100_80GB,
  'p5.4xlarge': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H100_80GB,
  'p5e.48xlarge': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H200_141GB,
  'p5en.48xlarge': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H200_141GB,

  // GCP
  'a2-highgpu-1g': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.A100_40GB,
  'a2-highgpu-2g': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.A100_40GB,
  'a2-highgpu-4g': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.A100_40GB,
  'a2-highgpu-8g': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.A100_40GB,
  'a2-megagpu-16g': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.A100_40GB,
  'a2-ultragpu-1g': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.A100_80GB,
  'a2-ultragpu-2g': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.A100_80GB,
  'a2-ultragpu-4g': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.A100_80GB,
  'a2-ultragpu-8g': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.A100_80GB,
  'a3-highgpu-1g': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H100_80GB,
  'a3-highgpu-2g': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H100_80GB,
  'a3-highgpu-4g': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H100_80GB,
  'a3-highgpu-8g': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H100_80GB,
  'a3-megagpu-8g': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H100_80GB,
  'a3-edgegpu-8g': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H100_80GB,
  'a3-edgegpu-8g-nolssd': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H100_80GB,

  // Oracle
  'bm.gpu4.8': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.A100_40GB,
  'bm.gpu.a100-v2.8': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.A100_80GB,
  'bm.gpu.h100.8': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H100_80GB,
  'vm.gpu.h100.1': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H100_80GB,
  'bm.gpu.h200.8': MIG_PROFILES_BY_NVIDIA_GPU_MODEL.H200_141GB,
};

export const getMigPartitionsByInstanceType = (
  instanceType: string
): string[] => {
  // Keys are lowercase since providers return mixed case (e.g. Oracle's 'BM.GPU4.8').
  const normalizedInstanceType = instanceType.toLowerCase();

  const exactMatch = MIG_PARTITIONS_BY_INSTANCE_TYPE[normalizedInstanceType];

  if (exactMatch) {
    return exactMatch;
  }

  // Nebius names are `platform.preset`, so the platform key matches by prefix.
  const [, migPartitions] =
    Object.entries(MIG_PARTITIONS_BY_NEBIUS_PLATFORM).find(([platform]) =>
      normalizedInstanceType.startsWith(platform)
    ) || [];

  return migPartitions ?? [];
};
